import React, { useEffect, useState } from 'react'
import styles from './KeyPickupDrop.module.css'
import { useDispatch } from 'react-redux';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import SorryPineapple from '../../images/pineapple-sorry.svg';
import { ToastType } from '../appointments/Appointments';
import { useGeolocation } from '../../hooks/geoLocation';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import Popup from '../common/popup/Popup';
import Toast from '../toast/Toast';
import LockIcon from '../icons/LockIcon';
import LeaasingNumberIcon from '../icons/LeasingNumberIcon';
import { getKeyPickupDropLocationVisit, getKeyPickupDropVisit } from '../../redux/keyPickupDropEventVisit/actions/keyPickupDropEventVisit.action';
import { useSelector } from 'react-redux';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import Loader from '../common/loader/Loader';
import SwiperButton from '../SwiperButton/SwiperButton';
import { openAmazonDoor } from '../../redux/appointment/actions/appointment.action';
import { AmazoneFlag } from '../../utils/constants/constants';

export interface keyPickupDropAccessCodeProps {
  eventId: string;
  latitude: string;
  longitude: string;
}

interface amazonDoorProps {
  locationId: string;
  aapointmentId?: string;
  vendorId?: string;
  eventId?: string;
  latitude: number;
  longitude: number;
  timestamp: number;
  amazonFlag: string;
}

const isAccessible = (
  keyPickupDropStartDate: string,
  keyPickupDropEndDate: string,
  keyPickupDropStartTime: string,
  keyPickupDropEndTime: string,
) => {
  // Use HH:mm for 24-hour format, hh:mm a for 12-hour format with AM/PM
  const startDate = moment(`${keyPickupDropStartDate} ${keyPickupDropStartTime}`, 'YYYY-MM-DD hh:mm a').toDate();

  const endDate = moment(`${keyPickupDropEndDate} ${keyPickupDropEndTime}`, 'YYYY-MM-DD hh:mm a').toDate();

  const currentDate = new Date();
  const startTimeRange = new Date(startDate.getTime() - 15 * 60000);
  const endTimeRange = new Date(endDate.getTime() + 15 * 60000);

  if (currentDate < startTimeRange) {
    return 0;
  } else if (currentDate >= startTimeRange && currentDate <= endTimeRange) {
    return 1;
  } else {
    return -1;
  }
};

const KeyPickupDrop = () => {
  const dispatch: AppDispatch = useDispatch();
  const { data: keyPickupDropEventData, loading: keyPickupDropEventLoading } = useSelector((state: IRootState) => state.keyPickupDropReducer);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<ToastType>('error');
  const [toastMessage, setToastMessage] = useState('');
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [accessCode, setAccessCode] = useState<null | string>(null);
  const { coordinates, isEnabled, askForPermissionIfNeeded, error: geoLocationError, isSupported } = useGeolocation();
  const [visitLoader, setVisitLoader] = useState(false);
  const [appointmentStatus, setAppointmentStatus] = useState(-1);
  const { id: eventIdFromUrl } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = React.useRef(false);


  useEffect(() => {
    // Prevent duplicate API calls by using a ref to track if data has been fetched
    if (eventIdFromUrl && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(getKeyPickupDropVisit(eventIdFromUrl));
    }
  }, [eventIdFromUrl, dispatch]);

  useEffect(() => {
    if (!keyPickupDropEventLoading && keyPickupDropEventData) {
      const accessStatus = isAccessible(
        keyPickupDropEventData?.startDate,
        keyPickupDropEventData?.endDate,
        keyPickupDropEventData?.startTime,
        keyPickupDropEventData?.endTime,
      );
      setAppointmentStatus(accessStatus);
    }
  }, [keyPickupDropEventLoading, keyPickupDropEventData]);

  const getPlatform = (): 'android' | 'ios' | 'default' => {
    const userAgentData = (navigator as any).userAgentData;

    if (userAgentData?.platform) {
      const platform = userAgentData.platform.toLowerCase();
      if (platform.includes('android')) return 'android';
      if (platform.includes('iphone') || platform.includes('ipad') || platform.includes('ios')) return 'ios';
    }

    const userAgent = navigator.userAgent.toLowerCase();
    if (/android/.test(userAgent)) return 'android';
    if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';

    if (
      navigator.platform === 'MacIntel' &&
      navigator.maxTouchPoints > 1 &&
      /safari/.test(userAgent) &&
      !/chrome|firefox|edg|opr/.test(userAgent) // Ensure it's not Ubuntu
    ) {
      return 'ios';
    }
    return 'default'; // If nothing matches
  };

  const getBrowserName = (): string => {
    const userAgent = navigator.userAgent.toLowerCase();
    const userAgentData = (navigator as any).userAgentData;

    // iOS Browsers
    if (userAgent.includes('crios')) return 'Chrome (iOS)';
    if (userAgent.includes('fxios')) return 'Firefox (iOS)';
    if (userAgent.includes('edgios')) return 'Edge (iOS)';

    // Chromium-based browsers
    if (userAgent.includes('duckduckgo')) return 'DuckDuckGo';
    if (userAgent.includes('vivaldi')) return 'Vivaldi';
    if (userAgent.includes('samsungbrowser')) return 'Samsung Internet';
    if (userAgent.includes('yabrowser')) return 'Yandex Browser';
    if (userAgent.includes('ucbrowser')) return 'UC Browser';

    // More reliable Brave detection
    const isBrave =
      (navigator as any).brave?.isBrave || // Check if Brave explicitly exposes itself
      userAgentData?.brands?.some((brand: { brand: string }) => brand.brand.toLowerCase() === 'brave'); // Check using userAgentData

    if (isBrave) return 'Brave';

    // Standard Browsers
    if (userAgent.includes('opr') || userAgent.includes('opera')) return 'Opera';
    if (userAgent.includes('chrome') && !userAgent.includes('edg') && !userAgent.includes('opr')) return 'Chrome';
    if (userAgent.includes('firefox')) return 'Firefox';
    if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'Safari';
    if (userAgent.includes('edg')) return 'Edge';
    if (userAgent.includes('trident') || userAgent.includes('msie')) return 'Internet Explorer';

    return 'Unknown';
  };

  const CustomModalBody = () => {
    // const platform: "android" | "ios" | "default" = getPlatform();
    const platform: 'android' | 'ios' | 'default' = getPlatform();
    const browser: string = getBrowserName();

    const instructions: Record<string, { message: string }> = {
      android: {
        message: `To enable location:\n<span>1️⃣ Open ${browser}.</span><span>2️⃣ Go to Settings > Site Settings.</span><span>3️⃣ Enable location for this website.</span>`,
      },
      ios: {
        message: `To enable location:\n<span>1️⃣ Open Settings.</span><span>2️⃣ Go to Privacy & Security > Location Services.</span><span>3️⃣ Enable location for ${browser}.</span>`,
      },
      default: {
        message: 'Please enable location permissions in your browser settings.',
      },
    };

    const { message } = instructions[platform] || instructions.default;

    return (
      <div className={styles.modalBodyContainer}>
        <div className={`${styles.modalHeader} allowSVG`}>
          <h3 className={styles.modalH3}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 2C7.6 2 4 5.6 4 10C4 15.4 11 21.5 11.3 21.8C11.5 21.9 11.8 22 12 22C12.2 22 12.5 21.9 12.7 21.8C13 21.5 20 15.4 20 10C20 5.6 16.4 2 12 2ZM12 19.7C9.9 17.7 6 13.4 6 10C6 6.7 8.7 4 12 4C15.3 4 18 6.7 18 10C18 13.3 14.1 17.7 12 19.7ZM12 6C9.8 6 8 7.8 8 10C8 12.2 9.8 14 12 14C14.2 14 16 12.2 16 10C16 7.8 14.2 6 12 6ZM12 12C10.9 12 10 11.1 10 10C10 8.9 10.9 8 12 8C13.1 8 14 8.9 14 10C14 11.1 13.1 12 12 12Z"
                fill="black"
              />
            </svg>
            <span>Enable Location Access</span>
          </h3>
        </div>
        <div className={styles.modalBodyInnerContainer}>
          <p className={styles.preWrappedMessage} dangerouslySetInnerHTML={{ __html: message }}></p>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.captureBtn} onClick={() => setShowCustomModal(false)}>
            Okay
          </button>
        </div>
      </div>
    );
  };

  const amazoneAccessDoor = async () => {
    if (isSupported && !isEnabled) {
      const locationAllowed = await askForPermissionIfNeeded();
      if (!locationAllowed) {
        openLocationSettings();
        return;
      }
    }
    if (isSupported && isEnabled && !geoLocationError && coordinates?.latitude && coordinates?.longitude) {
      const eventObj: amazonDoorProps = {
        locationId: keyPickupDropEventData?.locationId,
        eventId: keyPickupDropEventData?.id,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        timestamp: Math.floor(Date.now()),
        amazonFlag: AmazoneFlag.Event,
      };
      setIsLoading(true);
      onOpenAmazonDoor(eventObj);
    } else {
      setShowToast(true);
      setToastMessage('Please enable location permissions to unlock the door.');
      setToastType('error');
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    }
  };

  const onAmazonDoorOpenSuccess = () => {
    setToastMessage("Door opened successfully.");
    setShowToast(true);
    setToastType('success');
    setIsLoading(false);
  }

  const onAmazonDoorOpenFailure = (error: string) => {
    setToastMessage(error || "Unable to open door.");
    setShowToast(true);
    setToastType('error');
    setIsLoading(false);
  }

  const onOpenAmazonDoor = (eventObj: amazonDoorProps) => {
    dispatch(openAmazonDoor(eventObj, onAmazonDoorOpenSuccess, onAmazonDoorOpenFailure));
  };

  const onKioskSuccess = async (accessCode: string) => {
    setAccessCode(accessCode);
  };

  const onBMSSuccess = (message: string) => {
    setToastType('success');
    setShowToast(true);
    setToastMessage(message);
  };

  const onFailure = (message: string) => {
    setShowToast(true);
    setToastType('error');
    setToastMessage(message);
  };

  function openLocationSettings() {
    setShowCustomModal(true);
  }

  const getKeyPickupDropAccessCode = async () => {
    if (isSupported && isEnabled && coordinates?.latitude && coordinates?.longitude) {
      const eventAccessCodeData: keyPickupDropAccessCodeProps = {
        eventId: keyPickupDropEventData?.id,
        latitude: coordinates.latitude.toString(),
        longitude: coordinates.longitude.toString(),
      };
      dispatch(getKeyPickupDropLocationVisit(eventAccessCodeData, onKioskSuccess, onBMSSuccess, onFailure, setVisitLoader));
    }
    if (!isEnabled && geoLocationError) {
      openLocationSettings();
    }
    if (isSupported && !isEnabled) {
      const isAllowed = await askForPermissionIfNeeded();
      if (coordinates?.latitude && coordinates?.longitude && isAllowed) {
        const eventAccessCodeData: keyPickupDropAccessCodeProps = {
          eventId: keyPickupDropEventData?.id,
          latitude: coordinates.latitude.toString(),
          longitude: coordinates.longitude.toString(),
        };
        dispatch(getKeyPickupDropLocationVisit(eventAccessCodeData, onKioskSuccess, onBMSSuccess, onFailure, setVisitLoader));
      }
      if (!isAllowed) {
        openLocationSettings();
      }
    }
  };

  return (
    <div className={styles.container}>
      {(visitLoader || keyPickupDropEventLoading || isLoading) && <Loader />}
      {!keyPickupDropEventLoading && keyPickupDropEventData && appointmentStatus !== -1 ? (
        <>
          <div className={styles.upperContainer}>
            <div className={styles.appointmentTitle}>
              <h1>{keyPickupDropEventData?.name}</h1>
            </div>

            <div className={styles.inlineItems}>
              <div className={styles.detailWrapper}>
                <p className={styles.title}>Location Name</p>
                <p className={styles.info}>{keyPickupDropEventData?.locationName}</p>
              </div>
            </div>

            <div className={styles.inlineItems}>
              <div className={styles.detailWrapper}>
                <p className={styles.title}>Address</p>
                <p className={styles.info}>
                  {keyPickupDropEventData?.street1}, {keyPickupDropEventData?.city}, {keyPickupDropEventData?.state},{' '}
                  {keyPickupDropEventData?.zip}
                </p>
              </div>
            </div>

            <div className={styles.inlineItems}>
              <div className={styles.detailWrapper}>
                <p className={styles.title}>Start Date</p>
                <p className={styles.info}>
                  {keyPickupDropEventData?.startDate ? moment(keyPickupDropEventData?.startDate).format('MM-DD-YYYY') : '-'}
                </p>
              </div>
              <div className={styles.detailWrapper}>
                <p className={styles.title}>Start Time</p>
                <p className={styles.info}>
                  {keyPickupDropEventData?.startTime ? moment(keyPickupDropEventData?.startTime, 'HH:mm A').format('hh:mm A') : '-'}
                </p>
              </div>
            </div>

            <div className={styles.inlineItems}>
              <div className={styles.detailWrapper}>
                <p className={styles.title}>End Date</p>
                <p className={styles.info}>
                  {keyPickupDropEventData?.endDate ? moment(keyPickupDropEventData?.endDate).format('MM-DD-YYYY') : '-'}
                </p>
              </div>
              <div className={styles.detailWrapper}>
                <p className={styles.title}>End Time</p>
                <p className={styles.info}>
                  {keyPickupDropEventData?.endTime ? moment(keyPickupDropEventData?.endTime, 'HH:mm A').format('hh:mm A') : '-'}
                </p>
              </div>
            </div>

            <div className={styles.inlineItems}>
              <div className={styles.detailWrapper}>
                <p className={styles.title}>Locker Number</p>
                <p className={styles.info}>{keyPickupDropEventData?.locker}</p>
              </div>
              {/* <div className={styles.detailWrapper}>
                <p className={styles.title}>Event Name</p>
                <p className={styles.info}>{keyPickupDropEventData?.eventName}</p>
              </div> */}
            </div>


            {keyPickupDropEventData?.description === '' ? (
              ''
            ) : (
              <div className={styles.inlineItems}>
                <div className={styles.detailWrapper}>
                  <p className={styles.title}>Special Notes</p>
                  <p className={styles.info}>{keyPickupDropEventData?.description}</p>
                </div>
              </div>
            )}
            {keyPickupDropEventData.amazonKeyAccessId && appointmentStatus === 1 && (
              <div className={styles.amazoneBtn}>
                <SwiperButton loading={keyPickupDropEventLoading} onDragend={() => amazoneAccessDoor()} />
              </div>
            )}

            {<div className={styles.lowerBtn}>
              <div className={styles.infoBox}>
                <div className={styles.leftContainer}>
                  <LockIcon />
                </div>
                <div className={styles.rightContainer}>
                  <p>{keyPickupDropEventData?.lockerName?.toLowerCase() === 'kiosk' ? 'Kiosk Code' : 'BMS Locker'}</p>
                  <p
                    className={`${appointmentStatus !== 1 ? styles.noStyle : ''}`}
                    onClick={() => {
                      if (appointmentStatus === 1) {
                        getKeyPickupDropAccessCode()
                      }
                    }}>
                    {appointmentStatus === 1
                      ? keyPickupDropEventData?.lockerName?.toLowerCase() === 'kiosk'
                        ? accessCode || 'Tap here to access code'
                        : 'Tap here to open locker'
                      : keyPickupDropEventData?.lockerName?.toLowerCase() === 'kiosk'
                        ? 'Tap here to access code'
                        : 'Tap here to open locker'}
                  </p>
                </div>
              </div>
            </div>
            }

            {keyPickupDropEventData?.leasingAgentNumber && (
              <div className={styles.leasingContactBox}>
                <p className={styles.leasingText}>Have questions? Call us:</p>
                <div className={styles.leasingNumberBox}>
                  <a href={`tel:+${keyPickupDropEventData?.leasingAgentNumber}`} className={styles.leasingLink}>
                    <LeaasingNumberIcon />
                    <span>
                      {keyPickupDropEventData?.leasingAgentNumber
                        ? formatPhoneNumberIntl(`+${keyPickupDropEventData?.leasingAgentNumber}`)
                        : ''}
                    </span>
                  </a>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        !keyPickupDropEventLoading &&
        (!keyPickupDropEventData || !keyPickupDropEventData.data || appointmentStatus === -1) && (
          <div className={styles.noPropertyWrapper}>
            <img src={SorryPineapple} alt="no property" width={132} height={212} />
            <p>Events no longer active</p>
          </div>
        )
      )}

      {showCustomModal && (
        <Popup setOpen={setShowCustomModal} className="custom-webcam-modal" open={showCustomModal} bodyData={<CustomModalBody />} />
      )}
      {showToast && <Toast message={toastMessage} type={toastType || 'error'} />}
    </div>
  )
}

export default KeyPickupDrop
